import { useEffect, type RefObject } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/* While an overlay owns the screen, Tab must not walk out of it into the page
   behind. Focus enters on open and returns to whatever opened it on close.
   Shared by the mobile menu, and later the filter sheet and test-drive modal. */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
) {
  useEffect(() => {
    const container = ref.current
    if (!active || !container) return

    const opener = document.activeElement as HTMLElement | null

    const focusable = () =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      )

    /* An overlay is animating in when focus enters it, so it is usually still
       off the bottom of the screen — and the browser's helpful "scroll the
       focused thing into view" would then chase it down the page. The overlay
       is fixed; there is nothing to scroll to. */
    focusable()[0]?.focus({ preventScroll: true })

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const items = focusable()
      if (!items.length) return

      const first = items[0]
      const last = items[items.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      opener?.focus()
    }
  }, [active, ref])
}

export default useFocusTrap
